import axios from "axios";
import { ENV } from "../config";
import { Modal } from "antd";
import { SyncOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { FaRegCircleCheck } from "react-icons/fa6";
import moment from "moment";
const SECRET_KEY = "12345678901234567890123456789012";

// Helpers
const strToBuffer = (str) => new TextEncoder().encode(str);
const bufferToStr = (buffer) => new TextDecoder().decode(buffer);
const base64ToBuffer = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const bufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

export async function decrypt(encryptedString) {
  const [ivBase64, dataBase64] = encryptedString.split(":");

  const key = await crypto.subtle.importKey(
    "raw",
    strToBuffer(SECRET_KEY),
    { name: "AES-GCM" },
    false,
    ["decrypt"]
  );

  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: base64ToBuffer(ivBase64) },
    key,
    base64ToBuffer(dataBase64)
  );

  return bufferToStr(decrypted);
}

export async function encrypt(plainText) {
  const key = await crypto.subtle.importKey(
    "raw",
    strToBuffer(SECRET_KEY),
    { name: "AES-GCM" },
    false,
    ["encrypt"]
  );

  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    strToBuffer(plainText)
  );

  const ivBase64 = bufferToBase64(iv);
  const dataBase64 = bufferToBase64(encrypted);
  return `${ivBase64}:${dataBase64}`;
}

export const callWithPromise = async (method, myParameters, flag = "") => {
  try {
    if (method == "getUser") {
      const url = `${ENV.REACT_APP_REST_API}/users/getUserDetails/${myParameters.store_id}`;

      const config = {
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${myParameters.authorization}`,
          "x-refresh-token": myParameters.refreshToken,
        },
      };
      const response = await axios.get(url, config);
      return response.data.data;
    } else {
      const url = `${ENV.REACT_APP_REST_API}/user/${method}/${myParameters}`;
      const response = await axios.get(url);
      return response.data.data;
    }
  } catch (error) {
    // Equivalent to reject(err.error)
    throw error.response?.data?.message || error.message || "API Error";
  }
};

export const checkTextLength = (text, charLimitLength) => {
  if (text?.length > charLimitLength) {
    return text.substring(0, charLimitLength) + "...";
  } else {
    return text;
  }
};

export const convertToNumber = (value) => {
  if (value) {
    return Number(parseFloat(value).toFixed(2));
  } else {
    return 0;
  }
};

export const resolveTaxRate = (taxRate, fallbackRate = 16) => {
  const normalizedRate = String(taxRate ?? "").trim().toUpperCase();
  if (!normalizedRate) return fallbackRate;
  if (normalizedRate === "EX") return 0;
  const numericRate = Number(normalizedRate.replace("%", ""));
  return Number.isFinite(numericRate) ? numericRate : fallbackRate;
};

export const formatPaymentTermsForDisplay = (value = "") => {
  const ls_value = typeof value === "string" ? value.trim() : value;
  if (!ls_value) return value;
  const lo_date = moment(ls_value, "YYYY-MM-DD", true);
  return lo_date.isValid() ? lo_date.format("DD/MM/YYYY") : value;
};

export const formatDeliveryLabel = (value = "", fallback = "--") => {
  const ls_value = String(value || "").trim();
  if (!ls_value) return fallback;

  const lo_labelMap = {
    Order_Request: "Order Request",
    In_Fulfillment: "In Fulfillment",
    Store_Pickup: "Store Pickup",
    Order_Cancelled: "Order Cancelled",
  };

  if (lo_labelMap[ls_value]) return lo_labelMap[ls_value];
  if (!ls_value.includes("_")) return ls_value;

  return ls_value
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
};

export const isPickupDeliveryMethod = (value = "") =>
  String(value || "").trim().toLowerCase().endsWith("pickup");

export const isTerminalDispatchStatus = ({ delivery_status = "", transaction_status = "" } = {}) => {
  const ls_deliveryStatus = String(delivery_status || "").trim().toLowerCase();
  const ls_transactionStatus = String(transaction_status || "").trim().toLowerCase();

  return (
    ["delivered", "delivered(partial)"].includes(ls_deliveryStatus) ||
    (ls_deliveryStatus === "return" && ls_transactionStatus === "return")
  );
};

export const sortDefaultRankFirst = (items = [], nameKeys = []) => {
  const getRankSortValue = (obj = {}) =>
    Number.isFinite(Number(obj?.default_rank))
      ? Number(obj.default_rank)
      : Number.MAX_SAFE_INTEGER;
  const getDateSortValue = (obj = {}) => {
    const dateValue = obj?.created_at || obj?.created_date || "";
    const time = dateValue ? new Date(dateValue).getTime() : 0;
    return Number.isFinite(time) ? time : 0;
  };
  const getNameSortValue = (obj = {}) => {
    const keys = Array.isArray(nameKeys) ? nameKeys : [nameKeys];
    const key = keys.find((item) => obj?.[item]);
    return key ? String(obj[key]).toLowerCase() : String(obj?._id || "");
  };

  return [...items].sort((first, second) => {
    const rankDiff = getRankSortValue(first) - getRankSortValue(second);
    if (rankDiff !== 0) return rankDiff;

    const dateDiff = getDateSortValue(first) - getDateSortValue(second);
    if (dateDiff !== 0) return dateDiff;

    return getNameSortValue(first).localeCompare(getNameSortValue(second));
  });
};

export const getModuleWiseTab = (module = "", isHeadQuarter = false) => {
  switch (module) {
    case "purchase":
      return [
        { label: "All", value: "all" },
        { label: "New", value: "new" },
        { label: "Draft", value: "draft" },
        { label: "Mapped", value: "mapped" },
        { label: "UnMapped", value: "unmapped" },
        { label: "Rejected", value: "reject" },
        { label: "Expense", value: "expense" },
      ];
    case "incoming-transfer":
      return [
        { label: "All", value: "all" },
        { label: "Draft", value: "draft" },
        { label: "New", value: "new" },
        { label: "Request", value: "request" },
        // { label: "In Progress", value: "in_progress" },
        { label: "Completed", value: "completed" },
        { label: "Rejected", value: "rejected" },
      ];
    case "outgoing-transfer":
      return [
        { label: "All", value: "all" },
        { label: "Draft", value: "draft" }, 
        { label: "Request", value: "request" },
        { label: "Created", value: "created" },
        // { label: "In Progress", value: "in_progress" },
        { label: "Completed", value: "completed" },
        { label: "Rejected", value: "rejected" },
      ];
    case "import":
      return [
        { label: "All", value: "all" },
        { label: "New", value: "new" },
        { label: "Draft", value: "draft" },
        { label: "Mapped", value: "mapped" },
        { label: "UnMapped", value: "unmapped" },
        { label: "Rejected", value: "reject" },
      ];
    case "stock-analysis":
      return [
        { label: "All", value: "all" },
        { label: "Top Best Seller", value: "top_best_seller" },
        { label: "Best Seller", value: "best_seller" },
        { label: "Fast Moving", value: "fast_moving" },
        { label: "Average Performer", value: "average_performer" },
        { label: "Slow Moving", value: "slow_moving" },
        { label: "Dead Stock", value: "dead_stock" },
        { label: "New Stock", value: "new_stock" },
      ];
    case "warehouse-inventory":
      return [{ label: "All", value: "all" }];
    case "outgoing-order":
      return [
        { label: "All", value: "All" },
        { label: "Draft", value: "Draft" },
        { label: "Inquiry", value: "Inquiry" },
        { label: "Quoted", value: "Quoted" },
        { label: "Order Placed", value: "Order_Placed" },
        // ...(ENV.SHOW_LOCAL_FEATURES
        //   ? [
              { label: "In Fulfillment", value: "In_Fulfillment" },
              { label: "Dispatch", value: "Dispatch" },
              { label: "Completed", value: "Completed" },
        //   ]
        //   : []),
        { label: "Cancelled", value: "Cancelled" },
      ];
    case "selling-leads":
      return [
        { label: "All", value: "All" },
        { label: "Draft", value: "Draft" },
        { label: "Inquiry", value: "Inquiry" },
        { label: "Quoted", value: "Quoted" },
        { label: "Order Placed", value: "Order_Placed" },
        // ...(ENV.SHOW_LOCAL_FEATURES
        //   ? [
              { label: "In Fulfillment", value: "In_Fulfillment" },
              { label: "Dispatch", value: "Dispatch" },
              { label: "Completed", value: "Completed" },
        //   ]
        //   : []),
        { label: "Cancelled", value: "Cancelled" },
        { label: "Return", value: "Return" },
      ];
    case "stock-movement":
      return [
        { label: "Today", value: "today" },
        { label: "This Week", value: "this_week" },
        { label: "Last Week", value: "last_week" },
        { label: "This Month", value: "this_month" },
        { label: "Last Month", value: "last_month" }, 
        { label: "Custom", value: "custom" },
      ];
    default:
      break;
  }
}; 

// Buying Leads and Selling Leads share one collection, so each stores its drafts
// under its own order_status and shows them as plain "Draft"
export const BUYING_LEAD_DRAFT = "BL-Draft";
export const SELLING_LEAD_DRAFT = "SL-Draft";
// "Draft" is kept for buying-lead drafts saved before the split
const la_buyingLeadDraftStatuses = [BUYING_LEAD_DRAFT, "Draft"];

export const isBuyingLeadDraft = (status) => la_buyingLeadDraftStatuses.includes(status);
export const isSellingLeadDraft = (status) => status === SELLING_LEAD_DRAFT;
export const getOrderStatusLabel = (status) =>
  isBuyingLeadDraft(status) || isSellingLeadDraft(status) ? "Draft" : status;

export const getStatusIcon = (text = "pending") => {
  let icon = null;
  switch (text?.toLowerCase()) {
    case "processing":
      icon = <SyncOutlined spin style={{ color: "#0093ff" }} title={text} />;
      break;
    case "pending":
      icon = (
        <ClockCircleOutlined
          style={{ color: "rgb(183 156 17)" }}
          title={text}
        />
      );
      break;
    case "completed" || "Active":
      icon = (
        <FaRegCircleCheck
          style={{ color: "#379537" }}
          title={text}
        />
      );
      break;
    case "active":
      icon = (
        <FaRegCircleCheck
          style={{ color: "#379537", fontSize: 15 }}
          title={text}
        />
      );
      break;
    case "non_kra":
      icon = (
        <FaRegCircleCheck
          style={{ color: "rgb(135, 50, 168)", fontSize: 15 }}
          title={text}
        />
      );
      break;
    default:
      icon = null;
  }
  return icon;
};

export const productPriceCalculationBySellPrice = (
  allValues,
  tax_rate,
  value = ""
) => {
  let {
    prod_original_cstprice,
    prod_cstprice,
    profit_margin,
    prod_original_sellprice,
    prod_sellprice,
    profit,
    contigency_margin,
    contigency_amount,
    wholesale_price,
  } = allValues;

  if (value?.wholesale_price?.prod_sellprice) {
    let tax_percent = Number(tax_rate / 100);
    prod_sellprice = wholesale_price?.prod_sellprice;
    prod_original_cstprice = convertToNumber(prod_cstprice / (1 + tax_percent));
  }
  prod_original_sellprice = convertToNumber(
    (prod_sellprice * 100) / (100 + tax_rate)
  );
  let purchase_vat_tax = convertToNumber(
    prod_cstprice - prod_original_cstprice
  );
  let sales_vat_tax = convertToNumber(prod_sellprice - prod_original_sellprice);

  profit = convertToNumber(prod_original_sellprice - prod_original_cstprice);
  profit_margin =
    prod_original_cstprice === 0
      ? 100
      : convertToNumber((profit / prod_original_cstprice) * 100);
  let due_tax = convertToNumber(sales_vat_tax - purchase_vat_tax);

  if (value?.wholesale_price?.prod_sellprice) {
    return {
      wholesale_price: {
        prod_original_sellprice: prod_original_sellprice
          ? prod_original_sellprice
          : 0.0,
        prod_stkqty: wholesale_price?.prod_stkqty,
        prod_sellprice: value?.wholesale_price?.prod_sellprice
          ? value?.wholesale_price?.prod_sellprice
          : 0.0,
        prod_bulk_sellprice:
          convertToNumber(
            value?.wholesale_price?.prod_sellprice *
              wholesale_price?.prod_stkqty
          ) || 0.0,
        profit: profit ? profit : 0.0,
        profit_margin: profit_margin ? profit_margin : 0.0,
        contigency_amount: contigency_amount ? contigency_amount : 0.0,
        contigency_margin: contigency_margin ? contigency_margin : 0.0,
        purchase_vat_tax: purchase_vat_tax,
        sales_tax: sales_vat_tax ? sales_vat_tax : 0.0,
        due_tax: due_tax ? due_tax : 0.0,
        tax_type: "un-verified",
      },
    };
  }

  let lo_price = {
    prod_original_sellprice: prod_original_sellprice
      ? prod_original_sellprice
      : 0.0,
    profit: profit ? profit : 0.0,
    profit_margin: profit_margin ? profit_margin : 0.0,
    contigency_amount: contigency_amount ? contigency_amount : 0.0,
    contigency_margin: contigency_margin ? contigency_margin : 0.0,
    tax_values: {
      purchase_vat_tax: purchase_vat_tax,
      sales_vat_tax: sales_vat_tax ? sales_vat_tax : 0.0,
      due_tax: due_tax ? due_tax : 0.0,
      tax_type: "un-verified",
    },
  };
  return lo_price;
};

export const productPriceCalculation = (allValues, tax_rate) => {
  let tax_percent = Number(tax_rate / 100);
  let {
    prod_original_cstprice,
    prod_cstprice,
    profit_margin,
    prod_original_sellprice,
    prod_sellprice,
    profit,
    contigency_margin,
    contigency_amount,
  } = allValues;
  let profit_percent = Number(profit_margin / 100);
  prod_original_cstprice = convertToNumber(prod_cstprice / (1 + tax_percent));

  profit = convertToNumber(prod_original_cstprice * profit_percent);
  if (!isNaN(prod_original_cstprice)) {
    prod_original_sellprice = convertToNumber(prod_original_cstprice + profit);
  }
  if (contigency_margin) {
    handleContigencyMargin(allValues);
  }
  let midValue3 = Number(prod_original_sellprice * tax_percent);

  prod_sellprice = Math.round(
    convertToNumber(prod_original_sellprice + midValue3)
  );

  let purchase_vat_tax = convertToNumber(
    prod_cstprice - prod_original_cstprice
  );

  let sales_vat_tax = convertToNumber(prod_original_sellprice * tax_percent);

  purchase_vat_tax = purchase_vat_tax ? purchase_vat_tax : 0.0;
  let due_tax = convertToNumber(sales_vat_tax - purchase_vat_tax);

  let lo_price = {
    prod_original_cstprice: prod_original_cstprice
      ? prod_original_cstprice
      : 0.0,
    prod_original_sellprice: prod_original_sellprice
      ? prod_original_sellprice
      : 0.0,
    prod_sellprice: prod_sellprice ? prod_sellprice : 0.0,
    profit: profit ? profit : 0.0,
    contigency_amount: contigency_amount ? contigency_amount : 0.0,
    contigency_margin: contigency_margin ? contigency_margin : 0.0,
    tax_values: {
      purchase_vat_tax: purchase_vat_tax,
      sales_vat_tax: sales_vat_tax ? sales_vat_tax : 0.0,
      due_tax: due_tax ? due_tax : 0.0,
      tax_type: "un-verified",
    },
  };
  return lo_price;
};

export const calculateProductPrices = (row, isSellPriceChange, taxRate) => {
  const result = isSellPriceChange
    ? productPriceCalculationBySellPrice(row, taxRate)
    : productPriceCalculation(row, taxRate); 

  const prod_original_cstprice = isSellPriceChange
    ? Number(
        parseFloat(row.prod_cstprice / (1 + Number(taxRate / 100))).toFixed(2)
      )
    : result?.prod_original_cstprice ?? row.prod_original_cstprice;

  row["prod_original_cstprice"] = prod_original_cstprice;
  row["prod_original_sellprice"] = result?.prod_original_sellprice;

  row["prod_sellprice"] = isSellPriceChange
    ? row?.prod_sellprice
    : result?.prod_sellprice; 
  row["profit"] = result?.profit;
  row["tax_values"] = result?.tax_values;
  row["profit_margin"] = isSellPriceChange
    ? result?.profit_margin
    : row?.profit_margin;

    row["mapped_prod_sellprice"] = isSellPriceChange
    ? row?.prod_sellprice
    : result?.prod_sellprice;
    row["mapped_prod_original_sellprice"] = result?.prod_original_sellprice;
    row["mapped_profit"] = result?.profit;
    row["mapped_tax_values"] = result?.tax_values;
    row["mapped_profit_margin"] = isSellPriceChange
    ? result?.profit_margin
    : row?.profit_margin;
};

export const bulkPriceCalculation = (allValues, tax_rate, flag = "") => {
  let { prod_cstprice, prod_original_cstprice, wholesale_price } = allValues;

  if (wholesale_price || flag === "new_bulk_pricing") {
    let profit_margin = wholesale_price
      ? wholesale_price.profit_margin
      : allValues.profit_margin;
    let profit_percent = Number(profit_margin / 100);
    let tax_percent = Number(tax_rate / 100);
    prod_original_cstprice = convertToNumber(prod_cstprice / (1 + tax_percent));
    let profit = Number(
      parseFloat(prod_original_cstprice * profit_percent).toFixed(2)
    );

    let prod_original_sellprice = Number(
      parseFloat(
        (
          prod_original_cstprice +
          Number(prod_original_cstprice * profit_percent)
        ).toFixed(2)
      )
    );

    let prod_sellprice = Number(
      parseFloat(
        prod_original_sellprice + Number(prod_original_sellprice * tax_percent)
      ).toFixed(2)
    );

    let purchase_vat_tax = Number(
      parseFloat(prod_cstprice - prod_original_cstprice).toFixed(2)
    );
    let sales_tax = Number(
      parseFloat(prod_original_sellprice * tax_percent).toFixed(2)
    );
    let due_tax = Number(parseFloat(sales_tax - purchase_vat_tax).toFixed(2));
    if (flag === "new_bulk_pricing") {
      return {
        prod_original_sellprice,
        prod_sellprice,
        profit,
        tax_values: {
          sales_vat_tax: sales_tax,
          due_tax,
          purchase_vat_tax,
        },
      };
    }
    return {
      wholesale_price: {
        prod_original_sellprice,
        prod_sellprice,
        prod_stkqty: wholesale_price.prod_stkqty,
        prod_bulk_sellprice: convertToNumber(
          prod_sellprice * wholesale_price?.prod_stkqty
        ),
        profit,
        profit_margin,
        sales_tax,
        due_tax,
        purchase_vat_tax,
      },
    };
  }
};

export const calculateWholesalePrice = (
  isWholeSellChange,
  row,
  taxRate,
  key,
  newValue,
  bulk_price_no
) => {
  bulk_price_no = Number(bulk_price_no);
  if (!row || typeof newValue !== "number") {
    console.error("Invalid inputs provided.");
    return;
  }

  const isProfitMarginKey = key === "wholesale_profit_margin";
  const isSellPriceKey = key === "wholesale_prod_sellprice";

  if (
    (isWholeSellChange &&
      ((isProfitMarginKey && newValue > row.profit_margin) ||
        (isSellPriceKey && newValue > row.prod_sellprice))) ||
    isNaN(newValue)
  ) {
    showErrorMessage(key);
    return;
  } else if (
    isWholeSellChange &&
    ((isProfitMarginKey && newValue <= row.profit_margin) ||
      (isSellPriceKey && newValue <= row.prod_sellprice))
  ) {
    row[key] = newValue;

    // Ensure wholesale_price array exists
    if (!Array.isArray(row?.wholesale_price)) {
      row.wholesale_price = [];
    }

    // Update the wholesale_price array
    row.wholesale_price[bulk_price_no] = {
      ...row.wholesale_price[bulk_price_no], // Preserve existing data if it exists
      prod_original_sellprice:
        row?.wholesale_price[bulk_price_no]?.prod_original_sellprice ?? 0,
      prod_sellprice: row?.wholesale_prod_sellprice ?? 0,
      profit_margin:
        row?.wholesale_profit_margin ??
        row?.wholesale_price[bulk_price_no]?.profit_margin ??
        0,
      prod_stkqty:
        row?.wholesale_price[bulk_price_no]?.prod_stkqty ??
        row?.wholesale_prod_stkqty ??
        0,
      prod_bulk_sellprice:
        row?.wholesale_price[bulk_price_no]?.prod_bulk_sellprice ??
        row?.prod_bulk_sellprice ??
        0,
    };

    // Calculate the result based on the key
    const wholesale_obj = {
      wholesale_price: row.wholesale_price[bulk_price_no],
    };

    const calculationContext = {
      ...row,
      ...wholesale_obj,
    };
    const result = isSellPriceKey
      ? productPriceCalculationBySellPrice(
          calculationContext,
          taxRate,
          wholesale_obj
        )
      : bulkPriceCalculation(calculationContext, taxRate);

    // Apply the calculated result to the correct wholesale price entry
    if (result) {
      row["wholesale_price"] = row?.wholesale_price?.map((item, i) => {
        return i === bulk_price_no
          ? {
              ...item,
              ...result.wholesale_price,
              wholesale_price_no: `bulk_price_${bulk_price_no + 1}`,
            }
          : item;
      });
    }
  }
};

export const handleContigencyMargin = (allValues, tax_percent) => {
  let {
    contigency_margin,
    prod_original_cstprice,
    prod_original_sellprice,
    profit_margin,
    prod_sellprice,
  } = allValues;

  let contigency_percent = Number(contigency_margin / 100);
  let profit_percent = Number(profit_margin / 100);
  let contigency_amount;
  if (contigency_margin) {
    contigency_amount = convertToNumber(
      prod_original_cstprice * contigency_percent
    );

    if (isNaN(prod_original_cstprice)) {
      contigency_amount = convertToNumber(
        prod_original_sellprice * contigency_percent
      );
      let lo_midPrice = prod_original_sellprice + contigency_amount;

      prod_sellprice = convertToNumber(
        lo_midPrice + Number(lo_midPrice * tax_percent)
      );
    } else {
      // if (contigency_margin && !isNaN(prod_original_cstprice)) {
      contigency_amount = convertToNumber(
        prod_original_cstprice * contigency_percent
      );

      let midValue1 = Number(prod_original_cstprice * profit_percent);
      let midValue2 = Number(prod_original_cstprice * contigency_percent);

      prod_original_sellprice = convertToNumber(
        prod_original_cstprice + midValue1 + midValue2
      );
      let midValue3 = Number(prod_original_sellprice * tax_percent);
      prod_sellprice = convertToNumber(prod_original_sellprice + midValue3);
      // }
    }
  }

  return { contigency_amount, prod_original_sellprice, prod_sellprice };
};

export const showErrorMessage = (key) => {
  let msg =
    key === "wholesale_profit_margin"
      ? "Bulk profit markup can not be greater than to profit markup"
      : "Bulk Unit Selling Price (Incl Tax) can not be greater than to Selling Price";
  Modal.error({
    title: msg,
    bodyStyle: {
      backgroundColor: "rgba(221, 230, 252, 0.4)",
    },
  });
};

export const errorFormat = (error) => {
  console.log(typeof error, "typeof error");
  if (typeof error === "string") {
    return error;
  } else {
    const la_errorValues =
      error.length > 0 &&
      error.map((err) => {
        return err.value;
      });

    return la_errorValues?.join("\n");
  }
};

export const checkValidPhoneNoOrNot = (value) => {
  if (isNaN(value)) {
    return false;
  } else if (value.toString().length != 10) {
    return false;
  } else {
    var strFirstThree = String(value).substring(0, 3);
    if (strFirstThree == "254") {
      return false;
    }
    return true;
  }
};
